<template>
  <div class="summary-bar-selection" data-testid="summary-bar-selection">
    <div class="summary-bar-selection__card">
      <div class="large" :class="['large', { 'summary-bar-selection__error': globalErrors.length }]" data-testid="summary-bar-selection-text">
        {{ globalErrors[0] || selectionText }}
      </div>

      <div class="expandable-content" :class="{ 'is-expanded': hasSelections }">
        <div class="expandable-inner carousel-wrapper">
          <SummaryBarCarousel :list="selectedItems" step-by-step class="margin-top-xs" data-testid="summary-bar-thumbnails" @item-click="onItemClick">
            <template #item="{ data: item }">
              <div class="thumb-wrapper" data-testid="summary-bar-thumbnail-item">
                <Picture
                  v-if="item.imageUrl"
                  :key="item.upcId"
                  :lazy-load="false"
                  class="thumb-image"
                  :image-url="item.imageUrl"
                  :alt="item.alt || ''"
                  :image-sizes-by-breakpoints="imageSizesByBreakpoints"
                />
                <SummaryBarItemBadge :quantity="item.quantity" :error-message="failedUpcs?.[item.upcId]?.[0]" />
              </div>
            </template>
          </SummaryBarCarousel>
          <div class="margin-left-xs margin-right-xs">
            <header class="summary-bar-selection__subtotal h6 margin-top-xs" data-testid="summary-bar-subtotal">
              <span>Estimated total ({{ totalQuantity }})</span>
              <span data-testid="summary-bar-estimated-total">{{ estimatedTotal }}</span>
            </header>
          </div>
        </div>
      </div>

      <div class="summary-bar-selection__actions margin-top-xs">
        <BusyButton
          variant="emphasis"
          expanded
          :disabled="!hasSelections || isLoading"
          :is-loading="isAddToBagLoading"
          data-testid="summary-bar-add-to-bag"
          @click="onAddToBag"
        >
          Add to bag
        </BusyButton>
        <div class="expandable-content" :class="{ 'is-expanded': hasSelections }">
          <div class="expandable-inner">
            <BusyButton
              class="margin-top-xs"
              variant="secondary"
              expanded
              :disabled="!hasSelections || isLoading"
              :is-loading="isBuyNowLoading"
              data-testid="summary-bar-buy-now"
              @click="onBuyNow"
            >
              Buy now
            </BusyButton>
          </div>
        </div>
      </div>
    </div>

    <div class="expandable-content" :class="{ 'is-expanded': hasSelections }">
      <div class="expandable-inner">
        <div class="expand-footer-content margin-top-xs">
          <Button class="link-body" :variant="'link'" :disabled="isLoading" data-testid="summary-bar-clear-selections" @click="onClearSelections">
            Clear selections
          </Button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { storeToRefs } from 'pinia';

import Button from '@atomic-ui/button/Button.vue';
import Picture from '@atomic-ui/picture/Picture';
import BusyButton from '~/modules/ui/components/BusyButton.vue';
import SummaryBarCarousel from './SummaryBarCarousel.vue';
import SummaryBarItemBadge from './SummaryBarItemBadge.vue';

import { useMultiATBStore } from '~/stores/useMultiATBStore';
import { useCollectionSelectionStore, type SelectedItem } from '~/stores/useCollectionSelectionStore';
import { useUpc as useUpcMCL } from '@nuxt-common/media-card-large/app/composables/useUpc';

import { tagSTCClearAll } from '~/features/analytics/shopTheCollection';

import { useSummaryBarActions } from '../composables/useSummaryBarActions';

const imageSizesByBreakpoints = { small: 110, medium: 110, large: 110, mlarge: 110, xlarge: 110 };

const emit = defineEmits<(e: 'success') => void>();

const multiATBStore = useMultiATBStore();
const selectionStore = useCollectionSelectionStore();

const { totalQuantity, selections, estimatedTotal, hasSelections } = storeToRefs(selectionStore);
const { globalErrors, failedUpcs, isLoading, isAddToBagLoading, isBuyNowLoading } = storeToRefs(multiATBStore);

const { onAddToBag, onBuyNow } = useSummaryBarActions(() => {
  emit('success');
});

const selectionText = computed(() => (hasSelections.value ? 'Continue selecting items, then add them to your bag.' : 'Select the items you’d like.'));

const selectedItems = computed<SelectedItem[]>(() => {
  return Object.values(selections.value).filter((item: SelectedItem) => item.quantity > 0);
});

/**
 * Updates the selected UPC of a product to match the clicked item.
 */
const selectProductUpc = (productId: string, upcId: string) => {
  if (!productId || !upcId) return;

  const { selectedUpcNumber } = useUpcMCL(productId);
  if (selectedUpcNumber) {
    selectedUpcNumber.value = Number(upcId);
  }
};

/**
 * Performs a smooth scroll to the product element with offset padding.
 */
const scrollToProduct = (productId: string) => {
  const targetEl = document.getElementById(`image-container-${productId}`);
  if (!targetEl) return;

  const stickyHeaderEl = document.querySelector('.stc__header');
  const headerHeight = stickyHeaderEl ? stickyHeaderEl.getBoundingClientRect().height : 0;

  // Add extra padding so the product doesn't stick right below the header
  const extraPadding = 24;

  const elementPosition = targetEl.getBoundingClientRect().top + window.scrollY;
  const offsetPosition = elementPosition - headerHeight - extraPadding;

  window.scrollTo({
    top: offsetPosition,
    behavior: 'smooth',
  });
};

const onItemClick = ({ item }: { item: SelectedItem }) => {
  selectProductUpc(item.productId, item.upcId);
  scrollToProduct(item.productId);
};

const onClearSelections = () => {
  tagSTCClearAll();
  selectionStore.clearSelection();
};
</script>




<style lang="scss" scoped>
@import '~settingSCSS';

.summary-bar-selection {
  display: flex;
  flex-direction: column;

  .summary-bar-selection__card {
    display: flex;
    flex-direction: column;
    @include padding-xs();
    border: 1px solid $gray-5-color;
    background: $white;
  }

  .summary-bar-selection__error {
    color: $error-color;
  }

  /* CSS Grid Animation for Height */
  .expandable-content {
    display: grid;
    grid-template-rows: 0fr;
    transition:
      grid-template-rows 0.4s ease-in-out,
      opacity 0.3s ease-in-out;
    opacity: 0;

    &.is-expanded {
      grid-template-rows: 1fr;
      opacity: 1;
    }
  }

  .carousel-wrapper {
    margin-left: rem-calc(-16);
    margin-right: rem-calc(-16);
  }

  .expandable-inner {
    overflow: hidden;
    display: flex;
    flex-direction: column;
    min-height: 0;
  }

  .expand-footer-content {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .summary-bar-selection__subtotal {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .summary-bar-selection__actions {
    display: flex;
    flex-direction: column;
    width: 100%;
  }

  .thumb-wrapper {
    position: relative;
    width: rem-calc(110);
    height: rem-calc(137);
    flex-shrink: 0;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .thumb-image {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;

    :deep(img) {
      width: 100%;
      height: 100%;
      object-fit: cover;
      object-position: center;
    }
  }
}


@import '~settingSCSS';

.summary-bar-selection {
  .summary-bar-selection__card {
    border-radius: 5px;
  }
}
</style>